// File: frontend/src/components/YearProgressDots.js

import { Calendar, Moon } from 'lucide-react';
import { getWeekStart, calculateWeekCompletion } from '../utils/weekCalculations';
import { formatLocalDate } from '../utils/dateHelpers';
import { useMemo, useState, useRef, useCallback, memo } from 'react';

/**
 * Generates a random organic border-radius string
 * to create hand-drawn/irregular shapes.
 */
const getRandomBorderRadius = () => {
  const min = 40;
  const max = 60;

  const tl = Math.floor(Math.random() * (max - min) + min);
  const br = Math.floor(Math.random() * (max - min) + min);

  const tl2 = Math.floor(Math.random() * (max - min) + min);
  const tr2 = Math.floor(Math.random() * (max - min) + min);
  const br2 = Math.floor(Math.random() * (max - min) + min);
  const bl2 = Math.floor(Math.random() * (max - min) + min);

  return `${tl}% ${100 - tl}% ${br}% ${100 - br}% / ${tl2}% ${tr2}% ${br2}% ${bl2}%`;
};

/**
 * Generates a random delay for breathing animations
 */
const getRandomAnimationDelay = () => {
  return `${Math.random() * -4}s`;
};

// Memoized Dot Component for performance.
// Custom comparator: `day` is rebuilt on every parent render (see `weeks`
// useMemo + `allDays` spread), so default shallow memo always breaks. Compare
// the fields that actually affect output instead — this lets the 364 unaffected
// dots skip re-render on a single habit toggle.
const YearProgressDot = memo(({ day, dotSize, backgroundColor, isHovered, isRestingHighlight, cascadeDelay, onMouseEnter, onClick }) => {
  const hasCompleted = day.completedHabits && day.completedHabits.length > 0;
  const hasSick = day.sickHabits && day.sickHabits.length > 0;
  const hasAnyContent = hasCompleted || hasSick;

  let boxShadow;
  if (isRestingHighlight) {
    boxShadow = '0 0 0 2px #f59e0b, 0 0 0 4px rgba(245, 158, 11, 0.3)';
  } else if (day.isToday) {
    boxShadow = '0 0 0 2px #D4A574, 0 0 0 4px rgba(212, 165, 116, 0.3)';
  }

  return (
    // leading-none + flex: the button is inline-block by default, so the
    // wrapper would size to the inherited line-height (24px) even though the
    // button is 4px tall — that's what was giving landscape year-dots rows
    // ~20px of phantom vertical space each.
    //
    // `animate-fade-in-up` + cascadeDelay + margin used to live on a wrap
    // div in the parent (365 extra DOM elements). Folded in here so the
    // year view is 365 elements total instead of 730.
    <div
      className={`relative group/day flex leading-none animate-fade-in-up ${isHovered ? 'z-50' : 'z-0 hover:z-50'}`}
      style={{ animationDelay: cascadeDelay, animationFillMode: 'both', margin: '1.5px' }}
    >
      {/* Day tooltip: only mounted while hovered. Previously this div was
          always rendered with `opacity-0` so it could fade in via transition,
          which meant 365 invisible tooltips with shadow-xl + rounded-lg +
          nested content all carried paint/layer cost on every repaint. */}
      {isHovered && (
        <div
          className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-2 bg-[var(--color-ink-main)] text-[var(--color-paper-bg)] text-[0.625rem] rounded-lg shadow-xl min-w-[7.5rem] z-50 pointer-events-none"
        >
          <div className="font-bold border-b border-[var(--color-paper-bg)]/20 pb-1 mb-1 whitespace-nowrap">
            {day.date.toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' })}
          </div>
          <div className="flex flex-col space-y-0.5">
            {hasAnyContent ? (
              <>
                {day.completedHabits?.map(habit => (
                  <div key={habit.id} className="flex items-center space-x-1">
                    <span className="w-1 h-1 rounded-full bg-green-400 flex-shrink-0"></span>
                    <span className="truncate max-w-[9.375rem]">{habit.name}</span>
                  </div>
                ))}
                {day.sickHabits?.map(habit => (
                  <div key={`sick-${habit.id}`} className="flex items-center space-x-1">
                    <span className="w-1 h-1 rounded-full bg-amber-400 flex-shrink-0"></span>
                    <span className="truncate max-w-[9.375rem] text-amber-300">{habit.name} (resting)</span>
                  </div>
                ))}
              </>
            ) : (
              <span className="text-[var(--color-paper-bg)]/60 italic whitespace-nowrap">No goals completed</span>
            )}
          </div>
          {/* Arrow */}
          <div className="absolute top-full left-1/2 transform -translate-x-1/2 -mt-1 border-4 border-transparent border-t-[var(--color-ink-main)]"></div>
        </div>
      )}

      <button
        data-date={day.dateStr}
        onMouseEnter={() => onMouseEnter(day.dateStr)}
        onClick={() => onClick && onClick(day)}
        className={`
          ${dotSize} transition-all duration-300 cursor-pointer
          ${day.isToday ? 'z-10' : ''}
          ${isHovered ? 'scale-150 z-20' : 'hover:scale-125'}
        `}
        style={{
          borderRadius: day.borderRadius,
          animationDelay: day.animationDelay,
          backgroundColor,
          ...(boxShadow ? { boxShadow } : {})
        }}
      />
    </div>
  );
}, (prev, next) => {
  if (prev.backgroundColor !== next.backgroundColor) return false;
  if (prev.dotSize !== next.dotSize) return false;
  if (prev.isHovered !== next.isHovered) return false;
  if (prev.isRestingHighlight !== next.isRestingHighlight) return false;
  if (prev.cascadeDelay !== next.cascadeDelay) return false;
  if (prev.onMouseEnter !== next.onMouseEnter) return false;
  if (prev.onClick !== next.onClick) return false;
  const a = prev.day, b = next.day;
  if (a === b) return true;
  if (a.dateStr !== b.dateStr) return false;
  if (a.isToday !== b.isToday) return false;
  if (a.isValidDate !== b.isValidDate) return false;
  if (a.borderRadius !== b.borderRadius) return false;
  if (a.animationDelay !== b.animationDelay) return false;
  if ((a.completedHabits?.length || 0) !== (b.completedHabits?.length || 0)) return false;
  if ((a.sickHabits?.length || 0) !== (b.sickHabits?.length || 0)) return false;
  return true;
});

function YearProgressDots({ year, habits, completions, sickWeeks = {}, onClick, onWeekSelect, layoutMode = 'standard', userCreatedAt, config }) {
  const [hoveredDate, setHoveredDate] = useState(null);
  const containerRef = useRef(null);

  // Handle dot click - navigate to that week
  const handleDotClick = useCallback((day) => {
    if (onWeekSelect && day.isValidDate) {
      // Get the week start for this day
      const weekStart = getWeekStart(day.date);
      onWeekSelect(weekStart);
    }
  }, [onWeekSelect]);

  const today = new Date();
  const todayDateStr = formatLocalDate(today);

  let accountCreatedDateStr = null;
  if (userCreatedAt) {
    const createdDate = new Date(userCreatedAt);
    accountCreatedDateStr = formatLocalDate(createdDate);
  }

  // 1. Generate static calendar structure with random styles (Memoized on year/creation date)
  const calendarStructure = useMemo(() => {
    const startOfYear = new Date(year, 0, 1);
    const endOfYear = new Date(year, 11, 31);
    const structure = [];
    let currentDate = getWeekStart(startOfYear);

    while (currentDate <= endOfYear) {
      const weekDays = [];

      for (let i = 0; i < 7; i++) {
        const date = new Date(currentDate);
        date.setDate(currentDate.getDate() + i);

        if (date.getFullYear() === year) {
          const dateStr = date.toISOString().split('T')[0];
          const isBeforeCreation = accountCreatedDateStr && dateStr < accountCreatedDateStr;
          const isInFuture = dateStr > todayDateStr;
          const isValidDate = !isBeforeCreation && !isInFuture;

          weekDays.push({
            date,
            dateStr,
            isBeforeCreation,
            isInFuture,
            isValidDate,
            // Random visual properties (stable for this year/date)
            borderRadius: getRandomBorderRadius(),
            animationDelay: getRandomAnimationDelay()
          });
        }
      }

      if (weekDays.length > 0) {
        structure.push({
          weekStart: new Date(currentDate),
          days: weekDays
        });
      }

      currentDate.setDate(currentDate.getDate() + 7);
    }
    return structure;
  }, [year, accountCreatedDateStr, todayDateStr]);

  // 2. Merge completion data (Memoized on habits/completions/sickWeeks)
  const weeks = useMemo(() => {
    return calendarStructure.map(week => {
      const weekDateStrings = week.days.map(d => d.dateStr);
      const completion = calculateWeekCompletion(habits, weekDateStrings, completions, sickWeeks);

      // Get week start for sick weeks lookup
      const weekStartStr = formatLocalDate(getWeekStart(week.weekStart));

      let isWeekComplete = true;
      let hasAnyValidData = false;

      const daysWithData = week.days.map(day => {
        const isToday = day.dateStr === todayDateStr;
        let dayCompletion = 0;
        const completedHabits = [];
        const sickHabits = [];

        if (day.isValidDate && habits.length > 0) {
          const totalWeeklyGoals = habits.reduce((sum, habit) => sum + habit.weekly_goal, 0);
          if (totalWeeklyGoals > 0) {
            const completedGoalContribution = habits.reduce((sum, habit) => {
              // Check if habit is sick for this week
              const isSick = sickWeeks[`${habit.id}-${weekStartStr}`];
              if (isSick) {
                sickHabits.push(habit);
                return sum + habit.weekly_goal; // Sick counts as complete
              }
              if (completions[`${habit.id}-${day.dateStr}`]) {
                completedHabits.push(habit);
                return sum + habit.weekly_goal;
              }
              return sum;
            }, 0);
            dayCompletion = completedGoalContribution / totalWeeklyGoals;
          }
        }

        if (day.isValidDate) {
          hasAnyValidData = true;
          if (day.dateStr > todayDateStr) {
            isWeekComplete = false;
          }
        }

        return {
          ...day,
          isToday,
          completion: dayCompletion,
          month: day.date.getMonth(),
          completedHabits,
          sickHabits
        };
      });

      if (daysWithData.length < 7) {
        isWeekComplete = false;
      }

      return {
        ...week,
        weekStartStr,
        days: daysWithData,
        weekCompletion: completion,
        isComplete: isWeekComplete && hasAnyValidData
      };
    });
  }, [calendarStructure, habits, completions, sickWeeks, todayDateStr]);





  // "Living Paper" Colors. Return plain hex strings (not style objects) so
  // `YearProgressDot`'s memo comparator can do a cheap primitive compare —
  // a fresh `{ style: { backgroundColor } }` literal would break memo every
  // render even when the color is unchanged.
  const getDotColor = (day) => {
    if (day.isBeforeCreation || day.isInFuture) return 'var(--color-dot-disabled)';
    if (day.completion === 1) return '#16a34a';
    if (day.completion >= 0.75) return '#4ade80';
    if (day.completion >= 0.5) return '#f59e0b';
    if (day.completion > 0) return '#f87171';
    return 'var(--color-dot-empty)';
  };

  const getWeekDotColor = (weekCompletion) => {
    if (weekCompletion === 'excellent') return '#16a34a';
    if (weekCompletion === 'partial') return '#f59e0b';
    if (weekCompletion === 'minimal') return '#f87171';
    return 'var(--color-dot-empty)';
  };

  // 3. Flatten to single array with first-week spacers for alignment
  const allDays = useMemo(() => {
    var days = [];
    if (weeks.length > 0 && weeks[0].days.length < 7) {
      var padCount = 7 - weeks[0].days.length;
      for (var i = 0; i < padCount; i++) {
        days.push({ isSpacer: true, dateStr: 'spacer-' + i });
      }
    }
    weeks.forEach(function(week) {
      week.days.forEach(function(day) {
        days.push({ ...day, weekCompletion: week.weekCompletion, isWeekComplete: week.isComplete, weekStartStr: week.weekStartStr });
      });
    });
    return days;
  }, [weeks]);

  // 4. Set of week-start strings that contain at least one sick habit
  const restingWeekStarts = useMemo(() => {
    const weekStarts = new Set();
    Object.keys(sickWeeks).forEach(key => {
      if (sickWeeks[key]) {
        // Key format is "habitId-weekStartDate"
        const parts = key.split('-');
        // Last 3 parts are the date (YYYY-MM-DD)
        const weekStartDate = parts.slice(-3).join('-');
        weekStarts.add(weekStartDate);
      }
    });
    return weekStarts;
  }, [sickWeeks]);
  const weeksRestingCount = restingWeekStarts.size;

  // Resting-week highlight state: hover gives transient feedback, click pins it.
  const [restingHovered, setRestingHovered] = useState(false);
  const [restingPinned, setRestingPinned] = useState(false);
  const restingHighlightActive = restingHovered || restingPinned;



  // Touch Handlers
  const handleTouchMove = useCallback((e) => {
    const touch = e.touches[0];
    const element = document.elementFromPoint(touch.clientX, touch.clientY);
    const date = element?.getAttribute('data-date');

    if (date) {
      setHoveredDate(date);
    }
  }, []);

  const handleMouseLeave = useCallback(() => {
    setHoveredDate(null);
  }, []);

  return (
    <div
      className={`
        w-full h-full flex flex-col
        glass-morphism rounded-3xl backdrop-blur-xl
        ${config.padding}
        bg-[var(--color-paper-warm)]/40 border border-[var(--color-ink-subtle)]/10
        shadow-sm hover:shadow-md transition-all duration-500
      `}
      onMouseLeave={handleMouseLeave}
    >
      {/* Header - Minimal */}
      <div className="flex items-center justify-between mb-1 flex-shrink-0">
        <div className="flex items-center space-x-3">
          {config.showIcon && (
            <div className="p-2 bg-[var(--color-ink-main)]/5 rounded-xl">
              <Calendar className="w-5 h-5 text-[var(--color-ink-light)]" />
            </div>
          )}
          <div>
            <h2 className={`${config.headerSize} font-serif font-bold text-[var(--color-ink-main)]`}>
              {year}
            </h2>
          </div>
        </div>
        {weeksRestingCount > 0 && (
          <button
            type="button"
            onMouseEnter={() => setRestingHovered(true)}
            onMouseLeave={() => setRestingHovered(false)}
            onFocus={() => setRestingHovered(true)}
            onBlur={() => setRestingHovered(false)}
            onClick={() => setRestingPinned(p => !p)}
            aria-pressed={restingPinned}
            aria-label={`Highlight ${weeksRestingCount} resting ${weeksRestingCount === 1 ? 'week' : 'weeks'}`}
            className={`flex items-center space-x-1.5 px-2 py-1 bg-amber-100 dark:bg-amber-900/30 rounded-full cursor-pointer transition-shadow hover:bg-amber-200 dark:hover:bg-amber-900/50 focus:outline-none focus-visible:ring-2 focus-visible:ring-amber-400 ${restingPinned ? 'ring-2 ring-amber-400' : ''}`}
          >
            <Moon size={12} className="text-amber-600 dark:text-amber-400 fill-current" />
            <span className="text-xs font-medium text-amber-700 dark:text-amber-400">
              {weeksRestingCount} {weeksRestingCount === 1 ? 'week' : 'weeks'} resting
            </span>
          </button>
        )}
      </div>

      {/* Year Grid - Dense "Living Canvas" Layout */}
      <div
        className="flex-1 min-h-0 flex flex-col justify-center"
        ref={containerRef}
        onTouchStart={handleTouchMove}
        onTouchMove={handleTouchMove}
        onTouchEnd={() => setHoveredDate(null)}
      >
        <div className="flex flex-wrap content-center justify-left" style={{ margin: '-1.5px' }}>
          {allDays.map((day, index) => {
            if (day.isSpacer) {
              return <div key={day.dateStr} className={config.dotSize} style={{ margin: '1.5px', visibility: 'hidden' }} />;
            }
            const backgroundColor = day.isWeekComplete && day.isValidDate ? getWeekDotColor(day.weekCompletion) : getDotColor(day);
            return (
              <YearProgressDot
                key={day.dateStr}
                day={day}
                dotSize={config.dotSize}
                backgroundColor={backgroundColor}
                isHovered={hoveredDate === day.dateStr}
                isRestingHighlight={restingHighlightActive && !!day.weekStartStr && restingWeekStarts.has(day.weekStartStr)}
                cascadeDelay={`${index * 3}ms`}
                onMouseEnter={setHoveredDate}
                onClick={handleDotClick}
              />
            );
          })}
        </div>
      </div>

      {/* Legend. `space-x-*` (not `gap-*`) for item spacing because flex `gap`
          needs Chrome 84+ and silently no-ops on the Chrome 53 WebView. */}
      {config.showLegend && (
        <div className="mt-4 pt-4 border-t border-[var(--color-ink-subtle)]/10 flex flex-wrap items-center justify-between text-xs font-sans text-[var(--color-ink-light)]">
          <div className="flex items-center space-x-4">
            <div className="flex items-center space-x-1.5">
              <div className="w-2 h-2 rounded-full bg-green-600 shape-organic" aria-hidden="true" />
              <span>All</span>
            </div>
            <div className="flex items-center space-x-1.5">
              <div className="w-2 h-2 rounded-full bg-amber-500 shape-organic" aria-hidden="true" />
              <span>Some</span>
            </div>
            <div className="flex items-center space-x-1.5">
              <div className="w-2 h-2 rounded-full bg-red-400 shape-organic" aria-hidden="true" />
              <span>None</span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default memo(YearProgressDots);
